import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { FadeUp, SlideLeft, SlideRight } from '../../../components/shared/Motion';
import { EditableText, EditableImage, HrefPill } from '../../components';
import { getIcon } from '../iconMap';

function PillarsAlternating({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const items = data.items || [];
  const updateItem = (i, patch) => {
    const next = items.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update({ items: next });
  };
  return (
    <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
      <div className="container-page">
        <FadeUp className="text-center max-w-3xl mx-auto mb-16">
          <span className="eyebrow justify-center"><span className="h-px w-8 bg-olive" />
            <EditableText value={data.eyebrow} onChange={(t) => update({ eyebrow: t })} as="span" />
          </span>
          <h2 className="section-title mt-4">
            <EditableText value={data.heading_prefix} onChange={(t) => update({ heading_prefix: t })} as="span" />
            <span className="text-gradient"><EditableText value={data.heading_highlight} onChange={(t) => update({ heading_highlight: t })} as="span" /></span>
            <EditableText value={data.heading_suffix} onChange={(t) => update({ heading_suffix: t })} as="span" />
          </h2>
        </FadeUp>

        <div className="space-y-24 lg:space-y-32">
          {items.map((p, i) => {
            const PI = getIcon(p.icon);
            const ImgBlock = (
              <SlideLeft key="img">
                <div className="relative">
                  <div className="absolute -bottom-6 -left-6 w-44 h-44 opacity-50 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(161,201,164,0.45) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
                  <div className="relative overflow-hidden rounded-3xl shadow-[0_30px_60px_-25px_rgba(46,58,24,0.5)]">
                    <EditableImage
                      src={p.image?.src}
                      alt={p.image?.alt || p.title}
                      onChange={(src, alt) => updateItem(i, { image: { src, alt } })}
                      className="h-[460px] w-full object-cover hover:scale-[1.04] transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-olive-footer/35 to-transparent pointer-events-none" />
                  </div>
                </div>
              </SlideLeft>
            );
            const TextBlock = (
              <SlideRight key="text">
                <div>
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-olive-light text-olive shadow-[inset_0_0_0_1px_rgba(161,201,164,0.2)]">
                    <PI size={26} />
                  </div>
                  <div className="mt-5 text-[11px] font-mono uppercase tracking-[0.32em] text-olive">
                    Pillar · {String(i + 1).padStart(2, '0')}
                  </div>
                  <EditableText value={p.title} onChange={(t) => updateItem(i, { title: t })} as="h3" className="mt-2 font-display text-3xl lg:text-4xl text-ink leading-tight" />
                  <EditableText value={p.desc} onChange={(t) => updateItem(i, { desc: t })} as="p" multiline className="mt-5 text-ink/70 leading-relaxed text-lg" />
                  <Link to={data.cta_href || '#'} className="mt-7 inline-flex items-center gap-2 text-olive font-semibold text-sm group">
                    <EditableText value={data.cta_label || 'Learn more'} onChange={(t) => update({ cta_label: t })} as="span" />
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                  <HrefPill value={data.cta_href} onChange={(h) => update({ cta_href: h })} />
                </div>
              </SlideRight>
            );
            return (
              <div key={i} className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                {p.reverse ? [TextBlock, ImgBlock] : [ImgBlock, TextBlock]}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default PillarsAlternating;
